// Q Help: the one support widget every MHub product loads (built in
// TheRedBricks/mhub-web-turborepo, apps/mhub-q-help-widget). It shows Q's in-app chat. Bitrix24
// is cancelled (26 October 2026) and there is no form fallback: a customer Q doesn't admit, or
// can't reach, gets the widget's contact card (WhatsApp, phone, email). CRM mounts no Bitrix form
// of its own any more.
//
// The tag is page-level: injected once, outside React, and never removed. Only data-lang follows
// the app afterwards; the widget watches the attribute.

const LOADER_ATTR = 'data-mhub-q-help-loader';
const PROJECT = 'crm';

// CRM's i18n codes (en, id-ID, vi-VN) -> the codes the widget speaks.
const WIDGET_LANGUAGES = Object.freeze({
  en: 'en-GB',
  ms: 'ms-MY',
  id: 'id-ID',
  vi: 'vi-VN',
});

export const toWidgetLanguage = (language) => (
  typeof language === 'string'
    ? WIDGET_LANGUAGES[language.split('-')[0].toLowerCase()]
    : undefined
);

const syncLanguage = (script, language) => {
  const code = toWidgetLanguage(language);
  if (code && script.getAttribute('data-lang') !== code) {
    script.setAttribute('data-lang', code);
  }
};

// The widget reads the session from the shared Cognito cookies. It asks the app for a fresh id
// token, once, on open or send (never while idle), when the one there is within 30 s of expiry or
// Q refused it, so the refresh is forced: currentSession() would hand back a token it still calls
// valid. refreshSession() renews it with the refresh token and writes the new cookies on the
// shared domain, as currentSession() does for every query (the authLink). Signed out: it
// rejects, and the widget asks to refresh the page or sign in. Sign in reloads, and the authLink
// sends the user to log in and back.
const freshIdToken = async (auth) => {
  const user = await auth.currentAuthenticatedUser();
  const signedIn = user && typeof user.getSignInUserSession === 'function'
    ? user.getSignInUserSession()
    : null;
  if (!signedIn || typeof user.refreshSession !== 'function') {
    return (await auth.currentSession()).getIdToken().getJwtToken();
  }
  const session = await new Promise((resolve, reject) => {
    user.refreshSession(signedIn.getRefreshToken(), (error, fresh) => (
      error ? reject(error) : resolve(fresh)
    ));
  });
  return session.getIdToken().getJwtToken();
};

// Set before the tag goes in, so the widget finds it when it boots.
const setTokenProvider = (auth) => {
  if (!auth) return;
  const help = window.MHubQHelp || {};
  help.tokenProvider = () => freshIdToken(auth);
  window.MHubQHelp = help;
};

/**
 * Adds the widget's <script> to the page, once. No URL (REACT_APP_Q_HELP_WIDGET_URL unset for this
 * environment) means no tag at all.
 */
export function installQHelpWidget({
  src, version, i18n, auth,
} = {}) {
  if (!src || typeof document === 'undefined') return null;
  setTokenProvider(auth);

  const existing = document.querySelector(`script[${LOADER_ATTR}]`);
  if (existing) return existing;

  const script = document.createElement('script');
  script.src = src;
  script.defer = true;
  script.setAttribute(LOADER_ATTR, '');
  script.setAttribute('data-project', PROJECT);
  if (version) script.setAttribute('data-version', version);
  if (i18n) {
    syncLanguage(script, i18n.language);
    i18n.on('languageChanged', (language) => syncLanguage(script, language));
  }
  document.head.appendChild(script);
  return script;
}
